import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const projects = [
  {
    name: "Impresiones Plus",
    tagline: "Publicidad para empresas",
    logo: "/assets/impresiones-plus-logo.png",
    url: "https://impresionesplus.netlify.app/#top",
    testid: "case-card-impresiones-plus",
    index: "01",
    featured: true,
  },
  {
    name: "Leche de Toro",
    tagline: "Cafeteria de especialidad · CDMX · Ecosistema digital",
    logo: "https://customer-assets.emergentagent.com/wingman/17350a6b-6ba5-4913-87c3-995e6b7eed18/attachments/25bc08bb853348d78df75d81e4b7bcd0_image-Photoroom%20%286%29.png",
    url: "https://leche-toro-cdmx.preview.emergentagent.com",
    testid: "case-card-leche-de-toro",
    index: "02",
  },
  {
    name: "Grupo 1990",
    tagline: "Mori · Merequetengue · 1990 Café",
    logo: "https://customer-assets.emergentagent.com/wingman/17350a6b-6ba5-4913-87c3-995e6b7eed18/attachments/b63d91c63d67467d944aca1e3c801dd0_ChatGPT%20Image%2013%20jul%202026%2C%2001_15_48%20a.m..png",
    url: "https://ecosystem-portal-3.preview.emergentagent.com",
    testid: "case-card-grupo-1990",
    index: "03",
  },
];

const ProjectCard = ({ project }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 120, damping: 16 });
  const smy = useSpring(my, { stiffness: 120, damping: 16 });

  const rotateY = useTransform(smx, [-0.5, 0.5], [-4, 4]);
  const rotateX = useTransform(smy, [-0.5, 0.5], [4, -4]);
  const logoX = useTransform(smx, [-0.5, 0.5], [-14, 14]);
  const logoY = useTransform(smy, [-0.5, 0.5], [-14, 14]);
  const contentX = useTransform(smx, [-0.5, 0.5], [-6, 6]);

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.a
      ref={ref}
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={project.testid}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      whileHover={{ y: -10 }}
      transition={{ type: "spring", stiffness: 220, damping: 20 }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 1000 }}
      className={`group relative flex min-h-[420px] flex-col justify-between overflow-hidden rounded-3xl border bg-white/[0.03] p-8 backdrop-blur-xl transition-[border-color,box-shadow] duration-500 hover:border-white/35 hover:shadow-[0_0_50px_-22px_rgba(255,255,255,0.45)] md:min-h-[500px] md:p-12 ${
        project.featured
          ? "border-white/25 shadow-[0_0_60px_-26px_rgba(255,255,255,0.4)] md:col-span-2"
          : "border-white/10"
      }`}
    >
      {/* Inner cobalt haze */}
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-0 transition-opacity duration-700 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.09) 0%, transparent 70%)",
        }}
        aria-hidden="true"
      />

      <div className="flex items-start justify-between">
        <span className="font-display text-sm tracking-[0.3em] text-white/30">
          {project.index}
        </span>
        <ArrowUpRight
          className="h-6 w-6 text-white/40 transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-white"
          aria-hidden="true"
        />
      </div>

      <motion.div
        style={{ x: logoX, y: logoY }}
        className="flex flex-1 items-center justify-center py-6"
      >
        <img
          src={project.logo}
          alt={`${project.name} logo`}
          loading="lazy"
          className="h-48 w-48 object-contain transition-transform duration-700 ease-out group-hover:scale-105 md:h-64 md:w-64"
        />
      </motion.div>

      <motion.div style={{ x: contentX }}>
        <h3 className="font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
          {project.name}
        </h3>
        <p className="mt-3 text-sm text-white/50">{project.tagline}</p>
      </motion.div>
    </motion.a>
  );
};

export default function CasosExito() {
  return (
    <section
      data-testid="cases-section"
      className="relative mx-auto max-w-7xl px-5 py-28 md:px-10 md:py-40"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-end justify-between"
      >
        <div>
          <span className="text-[11px] font-medium tracking-[0.45em] text-white/45">
            PROYECTOS
          </span>
          <h2
            data-testid="cases-title"
            className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl"
          >
            Algunos casos de Éxito en proceso
          </h2>
        </div>
        <span className="hidden font-display text-sm tracking-[0.3em] text-white/30 md:block">
          ( 03 )
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="mt-14 grid gap-6 md:mt-20 md:grid-cols-2 md:gap-8"
      >
        {projects.map((p) => (
          <ProjectCard key={p.name} project={p} />
        ))}
      </motion.div>
    </section>
  );
}
